import { useConversation } from '@elevenlabs/react'
import { useAgentSignedUrl } from '@forwardflow/hooks/customHooks/useAgentSignedUrl'
import { useCallback, useRef, useEffect, useState } from 'react'

const activeConversationRef = {
  id: null as string | null,
  endFn: null as (() => Promise<void>) | null
}

interface ConversationButtonProps {
  agentId?: string
  voiceId: string
  firstMessage?: string
  prompt: string
  dynamicVariables: Record<string, any>
  className?: string
  onConversationStateChange?: (isActive: boolean) => void
  shouldStop?: boolean
}

export function ConversationalAI({
  voiceId,
  firstMessage,
  prompt,
  dynamicVariables,
  className = '',
  onConversationStateChange,
  shouldStop = false
}: ConversationButtonProps) {
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const conversationIdRef = useRef<string>(Math.random().toString(36).substring(2, 11))
  const [isLoading, setIsLoading] = useState(false)

  const { signedUrl, isLoading: isLoadingSignedUrl, error } = useAgentSignedUrl()

  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected')
      setIsLoading(false)
      activeConversationRef.id = conversationIdRef.current
      activeConversationRef.endFn = endCurrentSession
      onConversationStateChange?.(true)
    },
    onDisconnect: () => {
      stopMediaStream()
      setIsLoading(false)
      if (activeConversationRef.id === conversationIdRef.current) {
        activeConversationRef.id = null
        activeConversationRef.endFn = null
      }
      onConversationStateChange?.(false)
    },
    onMessage: (message) => console.log('Message:', message),
    onError: (error) => {
      console.error('Error:', error)
      stopMediaStream()
      setIsLoading(false)
    },
    overrides: {
      agent: {
        prompt: {
          prompt: prompt
        },
        firstMessage: firstMessage,
        language: 'en'
      },
      tts: {
        voiceId: voiceId
      }
    }
  })

  const stopMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop()
      })
      mediaStreamRef.current = null
    }
  }, [])

  const endCurrentSession = useCallback(async () => {
    setIsLoading(true)
    await conversation.endSession()
  }, [conversation])

  const toggleConversation = useCallback(async () => {
    if (!signedUrl || isLoadingSignedUrl) {
      console.error('Signed URL not available:', { signedUrl, isLoadingSignedUrl })
      return
    }

    if (error) {
      console.error('Cannot start conversation due to error:', error)
      return
    }

    try {
      if (conversation.status === 'connected') {
        await endCurrentSession()
      } else {
        setIsLoading(true)

        if (
          activeConversationRef.id &&
          activeConversationRef.id !== conversationIdRef.current &&
          activeConversationRef.endFn
        ) {
          await activeConversationRef.endFn()
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        mediaStreamRef.current = stream

        await conversation.startSession({
          signedUrl: signedUrl ?? '',
          dynamicVariables: dynamicVariables
        })
      }
    } catch (error) {
      console.error('Failed to toggle conversation:', error)
      stopMediaStream()
      setIsLoading(false)
    }
  }, [conversation, dynamicVariables, stopMediaStream, endCurrentSession])

  useEffect(() => {
    const cleanupConversationId = conversationIdRef.current
    return () => {
      stopMediaStream()
      if (activeConversationRef.id === cleanupConversationId) {
        activeConversationRef.id = null
        activeConversationRef.endFn = null
      }
    }
  }, [stopMediaStream])

  useEffect(() => {
    if (shouldStop && conversation.status === 'connected') {
      endCurrentSession()
    }
  }, [shouldStop, conversation.status, endCurrentSession])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && conversation.status === 'connected') {
        console.log('Tab is hidden, stopping conversation')
        endCurrentSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [conversation.status, endCurrentSession])

  useEffect(() => {
    const handleWindowBlur = () => {
      if (conversation.status === 'connected') {
        console.log('Window lost focus, stopping conversation')
        endCurrentSession()
      }
    }

    window.addEventListener('blur', handleWindowBlur)

    return () => {
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [conversation.status, endCurrentSession])

  const isConnected = conversation.status === 'connected'
  const isButtonDisabled = isLoading || isLoadingSignedUrl || !signedUrl || !!error

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    color: 'white',
    border: 'none',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    padding: 0,
    overflow: 'hidden',
    opacity: isButtonDisabled ? 0.6 : 1
  }

  const [isHovered, setIsHovered] = useState(false)
  const hoverStyle = isHovered ? { transform: 'scale(1.05)' } : {}

  const [isPressed, setIsPressed] = useState(false)
  const pressedStyle = isPressed ? { transform: 'scale(0.98)' } : {}

  const iconStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  }

  const svgStyle = {
    width: '32px',
    height: '32px',
    display: 'block',
    fill: 'white'
  }

  const spinnerContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  }

  const spinnerStyle = {
    width: '24px',
    height: '24px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 0.8s linear infinite'
  }

  useEffect(() => {
    const styleSheet = document.styleSheets[0]
    try {
      styleSheet.insertRule(
        `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `,
        styleSheet.cssRules.length
      )
    } catch (e) {
      console.log('Animation style already exists')
    }
  }, [])

  return (
    <button
      onClick={toggleConversation}
      disabled={isLoading}
      style={{ ...buttonStyle, ...hoverStyle, ...pressedStyle }}
      className={className}
      aria-label={isConnected ? 'Stop conversation' : 'Start conversation'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseOut={() => setIsPressed(false)}
    >
      {isLoading ? (
        <div style={spinnerContainerStyle}>
          <div style={spinnerStyle}></div>
        </div>
      ) : isConnected ? (
        <div style={iconStyle}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='currentColor'
            style={svgStyle}
          >
            <rect x='6' y='6' width='12' height='12' />
          </svg>
        </div>
      ) : (
        <div style={iconStyle}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='currentColor'
            style={svgStyle}
          >
            <path d='M8 5v14l11-7z' />
          </svg>
        </div>
      )}
    </button>
  )
}
